import type { PageLoader } from "@frontend/entrypoints/types";
import type { ResolvedComponent } from "@inertiajs/react";
import { ChunkRecoveryError } from "./diagnostics";
import { loadDefaultOrReload } from "./loadDefaultOrReload";

/**
 * Fixes "stale chunk" errors.
 *
 * Pages and lazy components load with dynamic import(). After a deploy, the
 * browser may still ask for an old file that no longer exists, so it 404s.
 * The fix: reload the page once to grab the new files. We only reload once
 * per 10s so a truly broken chunk can't loop forever.
 *
 * Split into small pieces:
 * - isChunkLoadError          — spot a chunk/import failure
 * - reloadGuard               — remember the last reload (sessionStorage → URL)
 * - reloadPage                — the guarded reload + halt-the-caller helper
 * - loadDefaultOrReload       — read a module's default export, reload if stale
 * - diagnostics               — what to report when recovery didn't work
 * - registerChunkErrorReload  — global vite:preloadError net
 *
 * See: https://vite.dev/guide/build#load-error-handling
 */
export { isChunkLoadError } from "./isChunkLoadError";
export { reloadPageOnChunkFailure, reloadAndHalt } from "./reloadPage";
export { loadDefaultOrReload } from "./loadDefaultOrReload";
export { registerChunkErrorReload } from "./registerChunkErrorReload";
export {
  buildChunkDiagnostics,
  ChunkRecoveryError,
  type ChunkDiagnostics,
  type ChunkFailureContext,
  type ChunkFailureReason,
} from "./diagnostics";
export {
  RELOAD_GUARD_KEY,
  RELOAD_PARAM,
  RELOAD_PAGE_AT_MOST_MS,
  RECOVERY_FAILED_MESSAGE,
} from "./constants";

/**
 * In production console.error is patched to forward its extra arguments into
 * Sentry's `extra`, so the second argument is what makes diagnostics visible on
 * the issue. See app/frontend/utils/sentry/index.ts.
 */
export const reportChunkFailure = (error: unknown): void => {
  if (error instanceof ChunkRecoveryError && error.diagnostics) {
    console.error(error, error.diagnostics);
    return;
  }
  console.error(error);
};

export async function validateInertiaPage(
  pageModule: PageLoader | undefined,
  pageName: string,
): Promise<ResolvedComponent> {
  if (!pageModule) {
    throw new Error(`Missing Inertia page component: '${pageName}/index.tsx'`);
  }

  return loadDefaultOrReload(pageModule, pageName);
}
